/**
 * Default IANA timezone from the user's browser (OS/region), e.g. Asia/Kolkata in India, Europe/Paris in France.
 * Used as the default for meetings, interviews, and mock interviews when opening the CRM.
 */

const IANA_ALIASES = {
  'Asia/Calcutta': 'Asia/Kolkata'
};

/**
 * @returns {string} IANA timezone, e.g. "Asia/Kolkata", "Europe/Paris"
 */
export function getBrowserIanaTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === 'string') {
      return IANA_ALIASES[tz] || tz;
    }
  } catch (_) {
    /* ignore */
  }
  return 'Asia/Kolkata';
}

function pickFromAllowedList(browserTz, allowedNames, fallbackFirst) {
  const names = (allowedNames || []).filter(Boolean);
  if (!names.length) return browserTz;
  if (names.includes(browserTz)) return browserTz;
  for (const [alias, canonical] of Object.entries(IANA_ALIASES)) {
    if (browserTz === alias && names.includes(canonical)) return canonical;
  }
  const parts = String(browserTz).split('/');
  if (parts.length >= 2) {
    const region = parts[0];
    const sameRegion = names.find((n) => n.startsWith(`${region}/`));
    if (sameRegion) return sameRegion;
  }
  return fallbackFirst ? names[0] : browserTz;
}

/**
 * Pick default timezone from Settings → Interview timezones (active rows).
 * Prefers an exact match with the browser zone, then same region (e.g. Europe/*), else first active.
 *
 * @param {Array<{ timezone_name?: string }>} activeZones
 * @param {string} [fallbackIfEmpty] when API returns no zones
 */
export function resolveDefaultInterviewTimezone(activeZones, fallbackIfEmpty) {
  const names = (activeZones || []).map((z) => z && z.timezone_name).filter(Boolean);
  if (!names.length) {
    return fallbackIfEmpty || getBrowserIanaTimezone();
  }
  return pickFromAllowedList(getBrowserIanaTimezone(), names, true);
}

/**
 * Pick default from a fixed list of IANA names (e.g. meeting forms).
 *
 * @param {string[]} allowedIANA
 * @param {string} [fallbackIfEmpty]
 */
export function resolveDefaultTimezoneFromList(allowedIANA, fallbackIfEmpty) {
  const list = Array.isArray(allowedIANA) ? allowedIANA.filter(Boolean) : [];
  if (!list.length) {
    return fallbackIfEmpty || getBrowserIanaTimezone();
  }
  return pickFromAllowedList(getBrowserIanaTimezone(), list, true);
}
